import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import SportsMenuViewTypes from '../types.js';
import { connect } from 'react-redux';
import { actions as SportsMenuActions } from 'modules/sports-menu/store/slice.js';

const SortingHelper = MojitoPresentation.Utils.SortingHelper;
const UIViewImplementation = MojitoCore.Presentation.UIViewImplementation;
const { IntentActionTrigger } = MojitoPresentation.Base.Intent;
const { NavigationPathBuilder } = MojitoPresentation.Base.Navigation;
const { HideablePane, Image, RotatableImage, Button, Text, FlexPane } =
    MojitoPresentation.Components;
const { SPORT_TYPES } = SportsMenuViewTypes;

class SportsMenuView extends UIViewImplementation {
    constructor(props) {
        super(props);

        this.onSportClicked = this.onSportClicked.bind(this);
        this.onFavoriteIconClicked = this.onFavoriteIconClicked.bind(this);
        this.handleToggleButtonClicked = this.handleToggleButtonClicked.bind(this);
    }

    componentDidMount() {
        this.props.dispatch(
            SportsMenuActions.setFavoriteSportsMaxAmount(this.config.highlightedSports.maxAmount)
        );
    }

    onSportClicked(_event, data) {
        this.emitAnalytics('sportsMenuSportClicked', {
            sportType: data.sportId,
            applicationMode: this.appContext().systemSettings().applicationMode,
        });
        this.props.cbSportClicked();
        if (data.sportType === SPORT_TYPES.VIRTUAL) {
            IntentActionTrigger.showVirtualSportsPage();
        } else {
            IntentActionTrigger.showSport(data.sportId);
        }
    }

    onFavoriteIconClicked(event, sportData) {
        const { dispatch } = this.props;
        event.stopPropagation();
        event.preventDefault();
        dispatch(SportsMenuActions.toggleFavoriteSport(sportData.sportId));
    }

    getFilteredSports(sports) {
        const hasName = sport => sport.name && sport.name.length > 0;
        const checkVirtuals = sport =>
            sport.type !== SPORT_TYPES.VIRTUAL || this.config.showVirtuals;
        return sports.filter(hasName).filter(checkVirtuals);
    }

    getSortedSportList() {
        const selectedLanguage = this.appContext().systemSettings().language;

        // Sort sports by name
        const sportNameSorter = (sportA, sportB) =>
            SortingHelper.compare(sportA.name, sportB.name, selectedLanguage);

        return this.getFilteredSports(this.props.availableSports).sort(sportNameSorter);
    }

    getHighlightedSportsList(allSports) {
        return this.props.highlightedSports
            .map(highlightedSport => {
                return allSports.find(sport => sport.id === highlightedSport.id);
            })
            .filter(Boolean)
            .slice(0, this.config.highlightedSports.maxAmount);
    }

    isFavoriteSport(sportId) {
        return this.props.highlightedSports.some(
            sport => sport.id === sportId && sport.isFavoriteSport
        );
    }

    handleToggleButtonClicked() {
        const { dispatch } = this.props;
        const isExpanded = !this.props.isExpanded;

        this.emitAnalytics('sportsMenuExpandedStateChanged', isExpanded);

        dispatch(SportsMenuActions.showAllSports(isExpanded));
    }

    renderToggleButton() {
        if (!this.config.toggleButton.show) {
            return null;
        }

        const seeAll = this.resolveString('$SPORTS_MENU_VIEW.SEE_ALL');
        const toggleButtonContainer = this.props.isExpanded
            ? this.style.expandedContainer
            : this.style.collapsedContainer;

        return (
            <div
                className="ta-toggleButton"
                style={toggleButtonContainer}
                onClick={this.handleToggleButtonClicked}
            >
                <RotatableImage
                    config={this.config.rotation}
                    angle={this.props.isExpanded ? '0deg' : '180deg'}
                />
                <div style={this.style.itemText}>
                    {this.props.isExpanded
                        ? this.resolveString('$SPORTS_MENU_VIEW.SEE_LESS')
                        : seeAll}
                </div>
            </div>
        );
    }

    renderSportItem(sport) {
        const imgSrc = this.config.sportIcons[sport.id] || this.config.sportIcons.defaultIcon;
        const isFavoriteSport = this.isFavoriteSport(sport.id);
        const favoriteIcon = isFavoriteSport
            ? this.config.favoriteIconToggled
            : this.config.favoriteIcon;
        const routeResolver = this.appContext().routeResolver;
        const routingData = NavigationPathBuilder.toSport(sport.id);

        const sportName =
            sport.type === SPORT_TYPES.VIRTUAL ? this.resolveString('$VIRTUALS.TITLE') : sport.name;

        return (
            <Button
                hrefLink={routeResolver.getRoute(routingData)}
                key={sport.id}
                onClickData={{ sportId: sport.id, sportType: sport.type }}
                onClick={this.onSportClicked}
                config={this.config.sportItem}
                class={`ta-SportItem ta-${sport.id}`}
            >
                <Image src={imgSrc} config={this.config.itemIcon} alt={sportName} />
                <Text config={this.config.itemContainer}>{sportName}</Text>
                <Button
                    onClick={this.onFavoriteIconClicked}
                    onClickData={{ sportId: sport.id }}
                    config={this.config.favoriteIconButton}
                    class={`ta-favorite-icon ta-${sport.id}${
                        isFavoriteSport ? ' ta-selected' : ''
                    }`}
                >
                    <Image config={favoriteIcon} />
                </Button>
            </Button>
        );
    }

    renderSports(sports) {
        return sports.map(sport => this.renderSportItem(sport));
    }

    renderSeparator() {
        if (!this.config.separator.show) {
            return;
        }

        return <div style={this.style.separator} />;
    }

    // Renders the titles above the sport lists e.g. 'Top sports' and 'All sports'
    renderTitle(title) {
        if (!this.config.listTitle.show) {
            return;
        }

        return <div style={this.style.listTitle}>{title}</div>;
    }

    renderAllSportsTitle() {
        return this.renderTitle(this.resolveString('$SPORTS_MENU_VIEW.ALL_SPORT_TITLE'));
    }

    renderHighlightedSportsTitle() {
        return this.renderTitle(this.resolveString('$SPORTS_MENU_VIEW.TOP_SPORT_TITLE'));
    }

    renderHeader() {
        const { header } = this.config.style;
        if (!this.config.header.show) {
            return;
        }
        const icon = header.icon.src ? <Image config={header.icon} /> : null;
        return (
            <div style={this.style.header}>
                {icon}
                <div style={this.style.headerText}>
                    {this.resolveString('$SPORTS_MENU_VIEW.HEADER')}
                </div>
            </div>
        );
    }

    renderExpandableSports(sports) {
        return (
            <HideablePane hidden={!this.props.isExpanded} config={this.config.allSportsContainer}>
                {this.renderSports(sports)}
            </HideablePane>
        );
    }

    render() {
        const sports = this.getSortedSportList();
        const highlightedSports = this.getHighlightedSportsList(sports);

        let separator, highlightedSportsListTitle, highlightedSportItems;
        // Highlighted sports section should be rendered if it's configured to be shown and there are highlighted sports to be rendered
        if (
            this.props.showHighlightedSports &&
            this.config.highlightedSports.show &&
            highlightedSports &&
            highlightedSports.length > 0
        ) {
            separator = this.renderSeparator();
            highlightedSportsListTitle = this.renderHighlightedSportsTitle();
            highlightedSportItems = this.renderSports(highlightedSports);
        }
        const sportItems = !this.config.toggleButton.show
            ? this.renderSports(sports)
            : this.renderExpandableSports(sports);

        return this.props.viewType === SportsMenuViewTypes.VIEW_TYPES.ONLY_HIGHLIGHTED ? (
            <FlexPane config={this.config.sportContainer} elementRef={this.props.elementRef}>
                {highlightedSportsListTitle}
                {highlightedSportItems}
            </FlexPane>
        ) : (
            <FlexPane
                config={this.config.container}
                elementRef={this.props.elementRef}
                class="ta-SportsMenu"
            >
                {this.renderHeader()}
                <FlexPane config={this.config.sportContainer}>
                    {highlightedSportsListTitle}
                    {highlightedSportItems}
                    {separator}
                    {this.renderAllSportsTitle()}
                    {this.renderToggleButton()}
                    {sportItems}
                </FlexPane>
            </FlexPane>
        );
    }
}

SportsMenuView.getStyle = function (config, mode, merge) {
    const toggleButtonContainer = {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        ...config.style.toggleButton,
    };

    return {
        header: merge(
            {
                display: 'flex',
                boxSizing: 'border-box',
                alignItems: 'center',
            },
            config.style.header
        ),
        headerText: merge(
            {
                textAlign: 'center',
            },
            config.style.headerText
        ),
        itemText: {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        },
        separator: config.style.separator,
        expandedContainer: merge(toggleButtonContainer, config.style.toggleButton.expanded || {}),
        collapsedContainer: toggleButtonContainer,
        listTitle: config.style.listTitle,
    };
};

export default connect()(SportsMenuView);
